/**
 * Editor for org template modules and their nested order forms.
 *
 * Renders an editable list of modules, each with ID, name, and a sub-list
 * of order forms. Supports add/remove at both module and order form levels.
 * Modules are displayed as numbered cards with visually nested order forms.
 * ID fields carry an InfoHint explaining the docx tag rules, a live copyable
 * tag preview, and inline validation errors passed down from the configurator.
 * Uses brand-compliant typography: text-overline on field labels, text-body-sm for body text.
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../common';
import InfoHint from '../common/InfoHint';
import TagPreview from './TagPreview';
import { generateId } from '../../utils/generateId';
import { moduleEnabledTag, orderFormEnabledTag } from '../../utils/templateTags';
import NumberedItemCard from './NumberedItemCard';
import type { OrgModule, OrgOrderForm } from '../../types/orgTemplate';

const MODULE_ID_HINT =
  "The ID becomes the conditional tag pair that wraps this module's section in the Word " +
  'document. Start with a letter and use only letters, numbers, hyphens and underscores — ' +
  'no spaces. Hyphens become underscores in the tag (clause IDs differ: their hyphens are kept).';

const ORDER_FORM_ID_HINT =
  "The ID is embedded in this module's order-form tag in the Word document. It is " +
  'lowercased in the tag and may start with a number. Use only letters, numbers, hyphens ' +
  'and underscores — no spaces.';

export interface ModuleEditorErrors {
  /** Module index → error message for that module's ID field. */
  moduleIdErrors: Record<number, string>;
  /** `${moduleIndex}.${orderFormIndex}` → error for that order form's ID field. */
  orderFormIdErrors: Record<string, string>;
}

interface ModuleEditorProps {
  modules: OrgModule[];
  onChange: (modules: OrgModule[]) => void;
  errors?: ModuleEditorErrors;
}

export default function ModuleEditor({ modules, onChange, errors }: ModuleEditorProps) {
  const handleAddModule = () => {
    onChange([...modules, { id: generateId('mod'), name: '', orderForms: [] }]);
  };

  const handleRemoveModule = (index: number) => {
    onChange(modules.filter((_, i) => i !== index));
  };

  const handleModuleChange = (index: number, field: keyof OrgModule, value: string) => {
    const updated = modules.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    );
    onChange(updated);
  };

  const handleOrderFormsChange = (moduleIndex: number, orderForms: OrgOrderForm[]) => {
    const updated = modules.map((m, i) =>
      i === moduleIndex ? { ...m, orderForms } : m
    );
    onChange(updated);
  };

  const handleAddOrderForm = (moduleIndex: number) => {
    const mod = modules[moduleIndex];
    handleOrderFormsChange(moduleIndex, [
      ...mod.orderForms,
      { id: generateId('of'), name: '' },
    ]);
  };

  const handleRemoveOrderForm = (moduleIndex: number, ofIndex: number) => {
    const mod = modules[moduleIndex];
    handleOrderFormsChange(
      moduleIndex,
      mod.orderForms.filter((_, i) => i !== ofIndex)
    );
  };

  const handleOrderFormChange = (
    moduleIndex: number,
    ofIndex: number,
    field: keyof OrgOrderForm,
    value: string
  ) => {
    const mod = modules[moduleIndex];
    const updatedForms = mod.orderForms.map((of, i) =>
      i === ofIndex ? { ...of, [field]: value } : of
    );
    handleOrderFormsChange(moduleIndex, updatedForms);
  };

  return (
    <div className="space-y-5">
      {modules.map((mod, mIdx) => (
        <NumberedItemCard
          key={mIdx}
          index={mIdx}
          title={mod.name || 'New Module'}
          identifier={mod.id || undefined}
          onRemove={() => handleRemoveModule(mIdx)}
          removeAriaLabel={`Remove module ${mod.name || mod.id}`}
        >
          <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-1 text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide">
                  Module ID
                  <InfoHint text={MODULE_ID_HINT} label="Module ID format" />
                </label>
                <input
                  type="text"
                  value={mod.id}
                  onChange={(e) => handleModuleChange(mIdx, 'id', e.target.value)}
                  aria-invalid={errors?.moduleIdErrors[mIdx] ? true : undefined}
                  className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-body-sm"
                  placeholder="e.g. ict-services"
                />
                {errors?.moduleIdErrors[mIdx] && (
                  <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                    {errors.moduleIdErrors[mIdx]}
                  </p>
                )}
                <div className="mt-1">
                  <TagPreview tag={mod.id ? moduleEnabledTag(mod.id) : ''} label="Module tag" />
                </div>
              </div>
              <div>
                <label className="block text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide">
                  Module Name
                </label>
                <input
                  type="text"
                  value={mod.name}
                  onChange={(e) => handleModuleChange(mIdx, 'name', e.target.value)}
                  className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-body-sm"
                  placeholder="e.g. ICT Services"
                />
              </div>
            </div>

            {/* Order Forms */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1 text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wide">
                  Order Forms ({mod.orderForms.length})
                  <InfoHint text={ORDER_FORM_ID_HINT} label="Order form ID format" />
                </span>
                <Button variant="ghost" size="sm" type="button" icon={<Plus className="h-3 w-3" />} onClick={() => handleAddOrderForm(mIdx)}>
                  Add Order Form
                </Button>
              </div>
              {mod.orderForms.length === 0 ? (
                <p className="text-body-sm text-slate-400 italic py-2">No order forms yet</p>
              ) : (
                <div className="bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                  {mod.orderForms.map((of, ofIdx) => (
                    <div key={ofIdx} className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-caption text-slate-400 font-mono w-5 text-right shrink-0">
                          {ofIdx + 1}.
                        </span>
                        <input
                          type="text"
                          value={of.id}
                          onChange={(e) => handleOrderFormChange(mIdx, ofIdx, 'id', e.target.value)}
                          aria-invalid={errors?.orderFormIdErrors[`${mIdx}.${ofIdx}`] ? true : undefined}
                          className="block w-1/3 rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-body-sm py-1"
                          placeholder="Form ID"
                        />
                        <input
                          type="text"
                          value={of.name}
                          onChange={(e) => handleOrderFormChange(mIdx, ofIdx, 'name', e.target.value)}
                          className="block flex-1 rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-body-sm py-1"
                          placeholder="Form Name"
                        />
                        <Button variant="danger" iconOnly size="sm" icon={<Trash2 className="h-3.5 w-3.5" />} aria-label={`Remove order form ${of.name || of.id}`} className="btn-icon-compact shrink-0" type="button" onClick={() => handleRemoveOrderForm(mIdx, ofIdx)} />
                      </div>
                      <div className="ml-8">
                        {errors?.orderFormIdErrors[`${mIdx}.${ofIdx}`] && (
                          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                            {errors.orderFormIdErrors[`${mIdx}.${ofIdx}`]}
                          </p>
                        )}
                        <TagPreview
                          tag={mod.id && of.id ? orderFormEnabledTag(mod.id, of.id) : ''}
                          label="Order form tag"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
        </NumberedItemCard>
      ))}

      <Button variant="secondary" size="sm" onClick={handleAddModule}>
        <Plus className="h-4 w-4 mr-1" />
        Add Module
      </Button>
    </div>
  );
}
